import { Link } from 'react-router-dom';

const PrivacyPolicy = () => {
  return (
    <div className="dashboard-container" data-testid="privacy-policy-page">
      <nav className="dashboard-nav">
        <Link to="/">
          <img src="/careguard-logo.png" alt="CareGuard" className="nav-logo" style={{ cursor: 'pointer' }} />
        </Link>
        <div className="nav-links">
          <Link to="/" className="nav-link">Home</Link>
          <Link to="/sms-terms" className="nav-link">SMS Terms</Link>
        </div>
      </nav>

      <div className="dashboard-content page-container" style={{ maxWidth: '800px', margin: '0 auto' }}>
        <div className="section-card">
          <h1 style={{ color: '#2c5364', marginBottom: '0.5rem', fontSize: '1.75rem' }}>Privacy Policy</h1>
          <p style={{ color: '#6b8899', marginBottom: '2rem', fontSize: '0.9rem' }}>Last Updated: April 15, 2026</p>

          <div style={{ lineHeight: '1.8', color: '#374151', fontSize: '0.95rem' }}>
            <p>CareGuard is an entity of Bryant Enterprises LLC, operating at careguardapp.com. CareGuard ("we," "our," or "us") is committed to protecting the privacy of our users. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use the CareGuard caregiver wellness monitoring application.</p>

            <h2 style={{ color: '#2c5364', marginTop: '2rem', fontSize: '1.25rem' }}>1. Information We Collect</h2>
            
            <h3 style={{ color: '#4a6572', fontSize: '1.05rem', marginTop: '1rem' }}>Personal Information</h3>
            <p>When you create an account or use our services, we may collect:</p>
            <ul style={{ paddingLeft: '1.5rem', marginBottom: '1rem' }}>
              <li>Full name, email address, and phone number(s)</li>
              <li>Age, county, and state of residence</li>
              <li>Medical conditions and health information you voluntarily provide</li>
              <li>Emergency contact names, phone numbers, email addresses, and relationships</li>
              <li>Client/patient information (name, address, age, health conditions) you voluntarily provide</li>
              <li>Social media profile URLs you voluntarily provide</li>
            </ul>

            <h3 style={{ color: '#4a6572', fontSize: '1.05rem', marginTop: '1rem' }}>Health & Wellness Data</h3>
            <ul style={{ paddingLeft: '1.5rem', marginBottom: '1rem' }}>
              <li>Vitals readings (blood pressure, heart rate, temperature, blood sugar, weight, oxygen saturation)</li>
              <li>Check-in status, feelings, and health concern notes</li>
              <li>Calendar events, appointments, and medication schedules</li>
            </ul>

            <h3 style={{ color: '#4a6572', fontSize: '1.05rem', marginTop: '1rem' }}>Documents</h3>
            <ul style={{ paddingLeft: '1.5rem', marginBottom: '1rem' }}>
              <li>Legal documents you upload (wills, healthcare directives, power of attorney, living trusts, insurance documents)</li>
              <li>Photos and media uploaded to your gallery</li>
            </ul>

            <h3 style={{ color: '#4a6572', fontSize: '1.05rem', marginTop: '1rem' }}>Location Data</h3>
            <ul style={{ paddingLeft: '1.5rem', marginBottom: '1rem' }}>
              <li>GPS coordinates and location address when you perform a check-in (only if you grant location permission)</li>
            </ul>

            <h3 style={{ color: '#4a6572', fontSize: '1.05rem', marginTop: '1rem' }}>Usage Data</h3>
            <ul style={{ paddingLeft: '1.5rem', marginBottom: '1rem' }}>
              <li>Check-in timestamps and history</li>
              <li>Alert status and escalation events</li>
              <li>Application interaction logs</li>
            </ul>

            <h2 style={{ color: '#2c5364', marginTop: '2rem', fontSize: '1.25rem' }}>2. How We Use Your Information</h2>
            <p>We use the information we collect to:</p>
            <ul style={{ paddingLeft: '1.5rem', marginBottom: '1rem' }}>
              <li><strong>Provide wellness monitoring:</strong> Send check-in reminders, track your safety status, and alert emergency contacts when check-ins are missed</li>
              <li><strong>Send notifications:</strong> Deliver SMS text messages, voice calls, and email alerts related to your wellness check-ins and safety</li>
              <li><strong>Emergency escalation:</strong> Contact your designated emergency contacts and, if enabled, local emergency services when you cannot be reached</li>
              <li><strong>Health tracking:</strong> Store and display your vitals and wellness data to help you monitor your health</li>
              <li><strong>Document storage:</strong> Securely store your uploaded legal documents for easy access</li>
              <li><strong>Calendar management:</strong> Manage your appointments, tasks, and medication schedules</li>
              <li><strong>Improve our service:</strong> Analyze usage patterns to enhance features and user experience</li>
            </ul>

            <h2 style={{ color: '#2c5364', marginTop: '2rem', fontSize: '1.25rem' }}>3. Information Sharing & Disclosure</h2>
            <p>We do <strong>not</strong> sell, trade, or rent your personal information to third parties. We may share your information in the following limited circumstances:</p>
            <ul style={{ paddingLeft: '1.5rem', marginBottom: '1rem' }}>
              <li><strong>Emergency contacts:</strong> Your designated emergency contacts will receive alerts containing your name, contact information, location (if enabled), and check-in status when you miss a scheduled check-in</li>
              <li><strong>Emergency services:</strong> If you have enabled automatic 911 dialing, your name, address, and client information may be shared with emergency services</li>
              <li><strong>Service providers:</strong> We use Twilio for SMS and voice notifications. Your phone number is shared with Twilio solely to deliver notifications. See <a href="https://www.twilio.com/legal/privacy" target="_blank" rel="noopener noreferrer" style={{ color: '#2c5364' }}>Twilio's Privacy Policy</a></li>
              <li><strong>Legal requirements:</strong> We may disclose your information if required by law, court order, or government request</li>
            </ul>

            <h2 style={{ color: '#2c5364', marginTop: '2rem', fontSize: '1.25rem' }}>4. Data Security</h2>
            <p>We implement industry-standard security measures to protect your personal information:</p>
            <ul style={{ paddingLeft: '1.5rem', marginBottom: '1rem' }}>
              <li>All data is transmitted over encrypted HTTPS connections</li>
              <li>Passwords are hashed using bcrypt encryption and never stored in plain text</li>
              <li>Authentication uses secure JSON Web Tokens (JWT)</li>
              <li>Documents and files are stored securely and accessible only to you</li>
              <li>Database access is restricted and monitored</li>
            </ul>
            <p>While we strive to protect your information, no method of electronic storage or transmission is 100% secure. We cannot guarantee absolute security.</p>

            <h2 style={{ color: '#2c5364', marginTop: '2rem', fontSize: '1.25rem' }}>5. Data Retention</h2>
            <p>We retain your personal information for as long as your account is active or as needed to provide you services. You may request deletion of your account and associated data at any time by contacting us. Upon account deletion, we will remove your personal data, check-in history, uploaded documents, and all related records.</p>

            <h2 style={{ color: '#2c5364', marginTop: '2rem', fontSize: '1.25rem' }}>6. SMS & Communications</h2>
            <p>By opting in to SMS notifications during registration, you consent to receive text messages from CareGuard. For full details, see our <Link to="/sms-terms" style={{ color: '#2c5364' }}>SMS Terms & Conditions</Link>.</p>
            <ul style={{ paddingLeft: '1.5rem', marginBottom: '1rem' }}>
              <li>Message frequency varies based on your check-in schedule (typically 2-10 messages per week)</li>
              <li>Message and data rates may apply</li>
              <li>Reply STOP to opt out of SMS at any time</li>
              <li>Reply HELP for SMS assistance</li>
            </ul>

            <h2 style={{ color: '#2c5364', marginTop: '2rem', fontSize: '1.25rem' }}>7. Your Rights</h2>
            <p>You have the right to:</p>
            <ul style={{ paddingLeft: '1.5rem', marginBottom: '1rem' }}>
              <li><strong>Access</strong> your personal information stored in your profile</li>
              <li><strong>Update</strong> or correct your information at any time through your profile settings</li>
              <li><strong>Delete</strong> your account and all associated data</li>
              <li><strong>Opt out</strong> of SMS notifications by replying STOP</li>
              <li><strong>Control</strong> notification preferences (SMS, voice, email) in your profile settings</li>
              <li><strong>Revoke</strong> location sharing permission through your browser settings</li>
            </ul>

            <h2 style={{ color: '#2c5364', marginTop: '2rem', fontSize: '1.25rem' }}>8. Children's Privacy</h2>
            <p>CareGuard is not intended for use by individuals under the age of 18. We do not knowingly collect personal information from children. If we become aware that we have collected information from a child under 18, we will take steps to delete that information.</p>

            <h2 style={{ color: '#2c5364', marginTop: '2rem', fontSize: '1.25rem' }}>9. Changes to This Policy</h2>
            <p>We may update this Privacy Policy from time to time. We will notify you of significant changes by posting the updated policy on this page with a revised "Last Updated" date. Your continued use of CareGuard after changes constitutes acceptance of the updated policy.</p>

            <h2 style={{ color: '#2c5364', marginTop: '2rem', fontSize: '1.25rem' }}>10. Contact Us</h2>
            <p>If you have questions about this Privacy Policy or your personal data, please contact us at:</p>
            <div style={{ background: '#f0f9ff', padding: '1rem', borderRadius: '8px', borderLeft: '4px solid #2c5364', marginTop: '0.5rem' }}>
              <p style={{ margin: 0 }} data-testid="privacy-company-name"><strong>CareGuard</strong>, an entity of Bryant Enterprises LLC</p>
              <p style={{ margin: '0.25rem 0 0' }} data-testid="privacy-company-address">850 Dogwood Road Suite B200 #2060, Lawrenceville, Georgia 30044, United States</p>
              <p style={{ margin: '0.25rem 0 0' }}>Email: support@careguardapp.com</p>
              <p style={{ margin: '0.25rem 0 0' }}>Website: <a href="https://www.careguardapp.com" style={{ color: '#2c5364' }}>careguardapp.com</a></p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
